import cx from "classnames";
/* eslint-disable max-lines */
import React, { CSSProperties, RefObject, useEffect, useRef, useState } from "react";

import { GameConceptFilter } from "../../helpers/gameConceptHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import * as styles from "./GameConceptFilterPopup.module.css";

type GameConceptFilterPopupProps = Readonly<{
  isOpen: boolean;
  onFilterSelected: (selectedFilters: GameConceptFilter[]) => void;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement>;
}>;

type FilterOption = {
  label: string;
  filters: GameConceptFilter[];
};

export const GameConceptFilterPopup = ({
  isOpen,
  anchorRef,
  onFilterSelected,
  onClose,
}: GameConceptFilterPopupProps) => {
  const [view, allSubmissions, underReview, needMoreInformation, rejected, approved] = useStrings(
    "view",
    "allSubmissions",
    "underReview",
    "needMoreInformation",
    "rejected",
    "approved"
  );
  const [popupWidth, setPopupWidth] = useState(0);
  const popupRef = useRef<HTMLDivElement>(null);
  const radioRefs = useRef<(HTMLInputElement | null)[]>([]);
  const options: FilterOption[] = [
    {
      label: allSubmissions,
      filters: [
        GameConceptFilter.UnderReview,
        GameConceptFilter.NeedMoreInfo,
        GameConceptFilter.Approved,
        GameConceptFilter.Rejected,
      ],
    },
    {
      label: underReview,
      filters: [GameConceptFilter.UnderReview],
    },
    {
      label: needMoreInformation,
      filters: [GameConceptFilter.NeedMoreInfo],
    },
    {
      label: approved,
      filters: [GameConceptFilter.Approved],
    },
    {
      label: rejected,
      filters: [GameConceptFilter.Rejected],
    },
  ];
  const [selectedFilterIndex, setSelectedFilterIndex] = useState<number>(0);

  useEffect(() => {
    const focusableElementsSelector = "input, button, [tabindex]:not([tabindex='-1'])";
    const focusableElements = popupRef.current?.querySelectorAll(focusableElementsSelector);
    const firstElement = focusableElements?.[0] as HTMLElement;
    const lastElement = focusableElements?.[focusableElements.length - 1] as HTMLElement;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }

      switch (event.key) {
        case "Tab":
          if (focusableElements?.length) {
            if (event.shiftKey) {
              // Shift + Tab
              if (document.activeElement === firstElement) {
                lastElement.focus();
                event.preventDefault();
              }
            } else {
              // Tab
              if (document.activeElement === lastElement) {
                firstElement.focus();
                event.preventDefault();
              }
            }
          }
          break;
        case "Escape":
        case "Enter":
          onClose();
          break;
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        !anchorRef.current?.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen && popupRef.current) {
      const popupRect = popupRef.current.getBoundingClientRect();
      setPopupWidth(popupRect.width);
      window.addEventListener("resize", onClose);
      document.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("keydown", handleKeyDown);

      // Set focus on the selected radio button
      if (radioRefs.current[selectedFilterIndex]) {
        radioRefs.current[selectedFilterIndex]?.focus();
      }
    }

    return () => {
      window.removeEventListener("resize", onClose);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchorRef, isOpen, onClose, selectedFilterIndex]);

  if (!isOpen) {
    return null;
  }

  const anchorPosition = anchorRef.current?.getBoundingClientRect();
  if (!anchorPosition) {
    return null;
  }

  // Calculate the initial left position
  let left = anchorPosition.left + window.scrollX + anchorPosition.width / 2 - popupWidth / 2;

  // Adjust the position if the popup overflows the viewport
  if (left + popupWidth > window.innerWidth) {
    left = window.innerWidth - popupWidth - 20;
  }

  const popupStyle: CSSProperties = {
    top: `${anchorPosition.bottom + window.scrollY + 10}px`,
    left: `${left}px`,
  };

  return (
    <div className={cx(commonStyles.rowContainer, styles.popup)} style={popupStyle} ref={popupRef}>
      <h5 className={styles.viewTitle}>{view}</h5>
      {options.map((option, index) => (
        <label
          key={`filter-${index}`}
          className={cx(styles.radioLabel, {
            [styles.radioLabelSelected]: selectedFilterIndex === index,
          })}
        >
          <input
            type="radio"
            ref={el => (radioRefs.current[index] = el)}
            value={index}
            checked={selectedFilterIndex === index}
            onChange={() => {
              setSelectedFilterIndex(index);
              onFilterSelected(option.filters);
            }}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
};
